import type { LocalizedDict } from '../index'

// Admin Settings → Integrations → Apple push notifications
// (features/admin/ApplePushCredentials.tsx). "Apple", "Key ID", "Team ID",
// "Bundle ID" and ".p8" are Apple's own names.

export type ApplePushStrings = {
  title: string
  encryptedNote: string
  simulationNotice: string
  loading: string
  loadError: string
  savedToast: string
  savedToastDescription: string
  saveErrorToast: string
  unknownError: string
  removedToast: string
  removeErrorToast: string
  providerSubtitle: string
  credentialsLabel: string
  updated: (when: string) => string
  remove: string
  confirmRemove: string
  yesRemove: string
  usageNote: string
  keyId: string
  keyIdHint: string
  teamId: string
  bundleId: string
  environment: string
  environmentProduction: string
  environmentSandbox: string
  p8Key: string
  p8KeyHint: string
  chooseFile: string
  fileReadError: string
  cancel: string
  saving: string
  rotateCredentials: string
  saveCredentials: string
}

export const applePushI18n: LocalizedDict<ApplePushStrings> = {
  en: {
    title: 'Apple push notifications',
    encryptedNote: 'The Apple key is encrypted at rest and remains write-only.',
    simulationNotice:
      'Simulation mode — the Apple key requires the live backend (real-API build). Saving is disabled here.',
    loading: 'Loading…',
    loadError: 'Could not load the Apple push key.',
    savedToast: 'Apple push key saved',
    savedToastDescription: 'The key is encrypted and only its Key ID is shown.',
    saveErrorToast: 'Could not save the key',
    unknownError: 'Unknown error',
    removedToast: 'Apple push key removed',
    removeErrorToast: 'Could not remove the key',
    providerSubtitle: 'Apple Push Notification service · one key for all of PEXIFY',
    credentialsLabel: 'Apple push key',
    updated: (when) => `Updated ${when}`,
    remove: 'Remove',
    confirmRemove: 'Remove the Apple push key?',
    yesRemove: 'Yes, remove',
    usageNote: 'This key sends the live delivery card to the iPhone lock screen.',
    keyId: 'Key ID',
    keyIdHint: '10 characters, shown next to the key in Apple Developer → Keys.',
    teamId: 'Team ID',
    bundleId: 'Bundle ID',
    environment: 'Environment',
    environmentProduction: 'Production',
    environmentSandbox: 'Sandbox',
    p8Key: '.p8 key',
    p8KeyHint: 'Paste the contents of the AuthKey file, or choose the file. Encrypted at rest and never displayed again.',
    chooseFile: 'Choose .p8 file',
    fileReadError: 'Could not read the file',
    cancel: 'Cancel',
    saving: 'Saving…',
    rotateCredentials: 'Replace key',
    saveCredentials: 'Save key',
  },
  tr: {
    title: 'Apple anlık bildirimleri',
    encryptedNote: 'Apple anahtarı şifreli saklanır ve yalnızca yazılabilir; geri okunamaz.',
    simulationNotice:
      'Simülasyon modu — Apple anahtarı için canlı sunucu gerekir (gerçek API sürümü). Burada kaydetme kapalı.',
    loading: 'Yükleniyor…',
    loadError: 'Apple bildirim anahtarı yüklenemedi.',
    savedToast: 'Apple bildirim anahtarı kaydedildi',
    savedToastDescription: 'Anahtar şifreli saklanır ve yalnızca Key ID gösterilir.',
    saveErrorToast: 'Anahtar kaydedilemedi',
    unknownError: 'Bilinmeyen hata',
    removedToast: 'Apple bildirim anahtarı silindi',
    removeErrorToast: 'Anahtar silinemedi',
    providerSubtitle: 'Apple bildirim servisi · tüm PEXIFY için tek anahtar',
    credentialsLabel: 'Apple bildirim anahtarı',
    updated: (when) => `Güncellendi: ${when}`,
    remove: 'Sil',
    confirmRemove: 'Apple bildirim anahtarı silinsin mi?',
    yesRemove: 'Evet, sil',
    usageNote: 'Bu anahtar, iPhone kilit ekranındaki canlı teslimat kartını gönderir.',
    keyId: 'Key ID',
    keyIdHint: '10 karakter; Apple Developer → Keys bölümünde anahtarın yanında yazar.',
    teamId: 'Team ID',
    bundleId: 'Bundle ID',
    environment: 'Ortam',
    environmentProduction: 'Canlı (Production)',
    environmentSandbox: 'Deneme (Sandbox)',
    p8Key: '.p8 anahtarı',
    p8KeyHint: 'AuthKey dosyasının içeriğini yapıştırın ya da dosyayı seçin. Şifreli saklanır ve bir daha gösterilmez.',
    chooseFile: '.p8 dosyası seç',
    fileReadError: 'Dosya okunamadı',
    cancel: 'Vazgeç',
    saving: 'Kaydediliyor…',
    rotateCredentials: 'Anahtarı değiştir',
    saveCredentials: 'Anahtarı kaydet',
  },
}
